import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import {
  HierarchyLabelFormSchema,
  HierarchyLabelsModel,
  type UpdateHierarchyLabelSortOrderParamsSchema,
} from '@/features/tenant-manager/models';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { queryKeyMap } from '../utils';

const BASE_URL = '/api/hierarchy-labels/';

type UseHierarchyLabelParams<T> = {
  params?: { enabledOnly: boolean };
  select?: (data: z.infer<typeof HierarchyLabelsModel>) => T;
  enabled?: boolean;
};

export const useHierarchyLabels = <T = z.infer<typeof HierarchyLabelsModel>>({
  params,
  select,
  enabled,
}: UseHierarchyLabelParams<T> = {}) => {
  const filterParams = {
    isUsedForCommentOrWorkflow: params?.enabledOnly ? true : undefined,
  };

  return useQuery({
    queryKey: queryKeyMap.hierarchyLabels(filterParams),
    queryFn: async () => {
      const hierarchyLabels = await axiosGet(BASE_URL, filterParams);
      return HierarchyLabelsModel.parse(hierarchyLabels);
    },
    select,
    enabled,
  });
};

type FormValues = z.infer<typeof HierarchyLabelFormSchema>;

export const useCreateHierarchyLabel = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation(
    {
      mutationFn: async (values: FormValues) => {
        const { ...payload } = HierarchyLabelFormSchema.parse(values);
        return await axiosPost(BASE_URL, payload);
      },
      onSuccess: async () => {
        await invalidate(queryKeyMap.hierarchyLabels());
      },
    },
    true,
  );
};

export const useUpdateHierarchyLabel = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, ...payload } = HierarchyLabelFormSchema.extend({
        id: z.string().uuid(),
      }).parse(values);
      return await axiosPatch(`${BASE_URL}${id}/`, payload);
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.hierarchyLabels());
    },
  });
};

export const useDeleteHierarchyLabel = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`${BASE_URL}${id}/`);
      return res;
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.hierarchyLabels());
    },
  });
};

export const useChangeOrderOfHierarchyLabel = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<typeof UpdateHierarchyLabelSortOrderParamsSchema>,
    ) => {
      const res = await axiosPost(
        '/api/hierarchy-labels/update-sort-order/',
        params,
      );
      return res;
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.hierarchyLabels());
    },
  });
};
